// src/pages/TrainingArgument.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingArgument() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [side, setSide] = useState('');
  const [loadingMotion, setLoadingMotion] = useState(false);

  const [point, setPoint] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [example, setExample] = useState('');
  const [link, setLink] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // New: structured feedback
  const [scores, setScores] = useState(null); // { argument_strength, structure, weighing, style_clarity, overall }
  const [comments, setComments] = useState('');
  const [improvements, setImprovements] = useState([]);
  // Legacy fallback text (still supported by backend)
  const [feedbackText, setFeedbackText] = useState('');

  const [touched, setTouched] = useState({ point: false, analysis: false, example: false, link: false });

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const generatePractice = async () => {
    setError('');
    setScores(null);
    setComments('');
    setImprovements([]);
    setFeedbackText('');
    setPoint(''); setAnalysis(''); setExample(''); setLink('');

    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');

      setMotion(data.motion);
      setSide(Math.random() < 0.5 ? 'Government' : 'Opposition');
    } catch (err) {
      console.error('Error generating motion:', err);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  const submitArgument = async () => {
    setTouched({ point: true, analysis: true, example: true, link: true });

    if (!point.trim() || !analysis.trim() || !example.trim() || !link.trim()) {
      setError('Please complete all four PAEL sections.');
      return;
    }
    if (!motion) {
      setError('Generate a motion first.');
      return;
    }

    setError('');
    setSubmitting(true);
    setScores(null);
    setComments('');
    setImprovements([]);
    setFeedbackText('');

    const fullArgument = `Point: ${point}\nAnalysis: ${analysis}\nExample: ${example}\nLink: ${link}`;

    try {
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motion, side, argument: fullArgument }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to get feedback');

      // New structured fields
      if (data.scores) setScores(data.scores);
      if (typeof data.comments === 'string') setComments(data.comments);
      if (Array.isArray(data.improvements)) setImprovements(data.improvements);

      // Legacy text (or friendly combined text from server)
      if (typeof data.feedback === 'string') setFeedbackText(data.feedback);
    } catch (err) {
      console.error(err);
      setError('Failed to get feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPAEL = async () => {
    const txt = `Point: ${point}\n\nAnalysis: ${analysis}\n\nExample: ${example}\n\nLink: ${link}`;
    try { await navigator.clipboard.writeText(txt); } catch {}
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Argument Building (PAEL)</h1>
          <p className="text-sm text-gray-600">Generate a realistic motion, write PAEL, and get rubric-based feedback.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generatePractice} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Motion */}
      <Card title="Practice Setup" subtitle="Your motion and assigned side">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
            <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
              {motion || 'Click "New Motion" to begin.'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Side</label>
            <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm min-h-[40px]">
              {side || '—'}
            </div>
          </div>
        </div>
      </Card>

      {/* PAEL form */}
      <Card title="Your PAEL">
        <div className="grid gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Point</label>
            <Textarea
              rows={2}
              value={point}
              onChange={(e) => setPoint(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, point: true }))}
              placeholder="What is your main argument?"
            />
            {touched.point && !point.trim() && (
              <p className="mt-1 text-xs text-red-600">Point is required.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Analysis</label>
            <Textarea
              rows={3}
              value={analysis}
              onChange={(e) => setAnalysis(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, analysis: true }))}
              placeholder="Why is this point true? How does it work?"
            />
            {touched.analysis && !analysis.trim() && (
              <p className="mt-1 text-xs text-red-600">Analysis is required.</p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Example</label>
              <Textarea
                rows={2}
                value={example}
                onChange={(e) => setExample(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, example: true }))}
                placeholder="Give a real or hypothetical example."
              />
              {touched.example && !example.trim() && (
                <p className="mt-1 text-xs text-red-600">Example is required.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Link Back</label>
              <Textarea
                rows={2}
                value={link}
                onChange={(e) => setLink(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, link: true }))}
                placeholder="How does this prove your side and why does it matter?"
              />
              {touched.link && !link.trim() && (
                <p className="mt-1 text-xs text-red-600">Link is required.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={copyPAEL}>Copy PAEL</Button>
            <Button onClick={submitArgument} disabled={submitting}>
              {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Scoring…</span> : 'Get Feedback'}
            </Button>
          </div>

          {error && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </Card>

      {/* Feedback */}
      {(scores || comments || improvements.length > 0 || feedbackText) && (
        <Card title="AI Feedback" subtitle="Scored against argument strength, structure, weighing, and clarity">
          <div className="space-y-4">
            {scores && (
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="grid sm:grid-cols-5 gap-2 text-sm text-gray-800">
                  {'argument_strength' in scores && (
                    <p><strong>Argument Strength:</strong> {scores.argument_strength}/10</p>
                  )}
                  {'structure' in scores && (
                    <p><strong>Structure:</strong> {scores.structure}/10</p>
                  )}
                  {'weighing' in scores && (
                    <p><strong>Weighing:</strong> {scores.weighing}/10</p>
                  )}
                  {'style_clarity' in scores && (
                    <p><strong>Style & Clarity:</strong> {scores.style_clarity}/10</p>
                  )}
                  {'overall' in scores && (
                    <p><strong>Overall:</strong> {scores.overall}/10</p>
                  )}
                </div>
              </div>
            )}

            {comments && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap text-sm text-gray-800">
                {comments}
              </div>
            )}

            {improvements.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="font-semibold text-sm text-gray-900 mb-2">Improvements</p>
                <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
                  {improvements.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
              </div>
            )}

            {/* Legacy or combined string from server */}
            {feedbackText && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap text-sm text-gray-700">
                {feedbackText}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
